import React, { useState, useEffect, useCallback, useRef } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  House, FileText, CheckCircle, XCircle, Clock, Download, UploadSimple,
  SpinnerGap, Funnel, Folders, Prohibit, Image as ImageIcon, PaperPlaneTilt, Package,
  ChatCircleDots, PencilSimple,
} from '@phosphor-icons/react';
import { fmtKsa } from '@/utils/formatKsa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const STATUS_META = {
  pending:  { ar: 'قيد المراجعة',       en: 'Pending',               cls: 'bg-amber-100 text-amber-800',    Icon: Clock },
  approved: { ar: 'مقبول',               en: 'Approved',              cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle },
  rejected: { ar: 'مرفوض',               en: 'Rejected',              cls: 'bg-red-100 text-red-800',        Icon: XCircle },
  cancelled_by_employee: { ar: 'ملغى من قبل الموظف', en: 'Cancelled by employee', cls: 'bg-slate-200 text-slate-700 border border-slate-300', Icon: XCircle },
};

export default function AdminRequests() {
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { user, isAdmin } = useAuth();
  const { empNo } = useEmployeesMap();
  const isAr = language === 'ar';
  const direction = isRTL ? 'rtl' : 'ltr';
  // The IT manager (fixed email) has a stripped-down view: sees only
  // id_card requests that have been forwarded to IT, and can only mark
  // them as delivered. No approve/reject/upload/reject actions.
  const isITOnly = !!user?.is_it_manager && !isAdmin;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [types, setTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('inbox');

  // Letters library: all approved requests with attachments
  const [letters, setLetters] = useState([]);
  const [lettersLoading, setLettersLoading] = useState(false);
  const [lettersTypeFilter, setLettersTypeFilter] = useState('');

  const [replyDialogRow, setReplyDialogRow] = useState(null);
  const [replyText, setReplyText] = useState('');
  // Edit-reply dialog — post-approval edit of an existing admin reply.
  // `editReplyRow` holds the request being edited; `editReplyText` its
  // current text. A separate flow from the initial approval reply so we
  // never accidentally re-approve a rejected/pending request.
  const [editReplyRow, setEditReplyRow] = useState(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [editReplySaving, setEditReplySaving] = useState(false);
  const [rejectDialogId, setRejectDialogId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [revokeDialogId, setRevokeDialogId] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [acting, setActing] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingUpload, setPendingUpload] = useState(null); // request id awaiting file pick
  // After the admin picks a file we open a small confirmation dialog so
  // they can choose whether the PDF should be QR-stamped or delivered
  // exactly as uploaded. { id, file } | null
  const [qrDialog, setQrDialog] = useState(null);
  const [photoDialogUrl, setPhotoDialogUrl] = useState(null); // employee-side photo preview

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const [reqRes, typesRes] = await Promise.all([
        axios.get(`${API}/requests`, { params, withCredentials: true }),
        axios.get(`${API}/requests-catalog/types`, { withCredentials: true }),
      ]);
      setItems(reqRes.data || []);
      setTypes(typesRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Split into two streams: standard letter/service requests (inbox) vs.
  // "Other requests" — free-form employee questions that live in their own
  // "Employee Communication" tab and are answered with a plain-text reply.
  const inboxItems = items.filter((r) => r.type !== 'other');
  const communicationItems = items.filter((r) => r.type === 'other');
  const inboxTypes = types.filter((t) => t.value !== 'other');

  const fetchLetters = useCallback(async () => {
    setLettersLoading(true);
    try {
      const params = { status: 'approved' };
      if (lettersTypeFilter) params.type = lettersTypeFilter;
      const { data } = await axios.get(`${API}/requests`, { params, withCredentials: true });
      // Only those with an uploaded letter file
      setLetters((data || []).filter((r) => r.has_attachment));
    } catch (e) {
      console.error(e);
    } finally {
      setLettersLoading(false);
    }
  }, [lettersTypeFilter]);

  useEffect(() => {
    if (activeTab === 'library') fetchLetters();
  }, [activeTab, fetchLetters]);

  const handleApprove = async (id, opts = {}) => {
    setActing(id);
    try {
      const body = opts.reply ? { reply: opts.reply } : {};
      await axios.post(`${API}/requests/${id}/approve`, body, { withCredentials: true });
      toast.success(isAr ? 'تم الاعتماد' : 'Approved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الاعتماد' : 'Approve failed'));
    } finally {
      setActing(null);
    }
  };

  const handleApproveWithReply = async () => {
    if (!replyDialogRow) return;
    const text = (replyText || '').trim();
    if (!text) {
      toast.error(isAr ? 'يرجى كتابة الرد قبل الاعتماد' : 'Please write a reply before approving');
      return;
    }
    await handleApprove(replyDialogRow.id, { reply: text });
    setReplyDialogRow(null);
    setReplyText('');
  };

  const handleReject = async () => {
    if (!rejectDialogId) return;
    setActing(rejectDialogId);
    try {
      await axios.post(`${API}/requests/${rejectDialogId}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success(isAr ? 'تم الرفض' : 'Rejected');
      setRejectDialogId(null);
      setRejectReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الرفض' : 'Reject failed'));
    } finally {
      setActing(null);
    }
  };

  // Post-approval edit of the admin's reply. Only enabled on approved
  // rows; backend rejects any attempt to edit a pending/rejected doc.
  // The employee is notified so they know the answer was updated.
  const openEditReply = (row) => {
    setEditReplyRow(row);
    setEditReplyText(row.admin_reply_text || '');
  };
  const handleSaveEditedReply = async () => {
    if (!editReplyRow) return;
    const text = (editReplyText || '').trim();
    if (!text) {
      toast.error(isAr ? 'الرد لا يمكن أن يكون فارغاً' : 'Reply cannot be empty');
      return;
    }
    setEditReplySaving(true);
    try {
      await axios.patch(
        `${API}/requests/${editReplyRow.id}/reply`,
        { reply: text },
        { withCredentials: true },
      );
      toast.success(isAr ? 'تم تحديث الرد' : 'Reply updated');
      setEditReplyRow(null);
      setEditReplyText('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل تحديث الرد' : 'Update failed'));
    } finally {
      setEditReplySaving(false);
    }
  };

  // Admin-only: forward an approved id_card request to the IT manager.
  const handleForwardToIT = async (id) => {
    if (!window.confirm(isAr
      ? 'سيتم تحويل الطلب إلى مدير تقنية المعلومات لاتخاذ إجراء الطباعة/التسليم. متابعة؟'
      : "Forward this request to IT for printing/handover. Continue?")) return;
    setActing(id);
    try {
      await axios.post(`${API}/requests/${id}/forward-to-it`, {}, { withCredentials: true });
      toast.success(isAr ? 'تم التحويل لمدير تقنية المعلومات' : 'Forwarded to IT manager');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل التحويل' : 'Forward failed'));
    } finally {
      setActing(null);
    }
  };

  // IT-only: mark an id_card request as printed/delivered.
  const handleITComplete = async (id) => {
    if (!window.confirm(isAr
      ? 'تأكيد إغلاق الطلب: تم إعداد بطاقة التعريف وتسليمها للموظف؟'
      : 'Confirm closing: ID card prepared and handed over to the employee?')) return;
    setActing(id);
    try {
      await axios.post(`${API}/requests/${id}/it-complete`, {}, { withCredentials: true });
      toast.success(isAr ? 'تم إغلاق الطلب' : 'Request closed');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الإغلاق' : 'Close failed'));
    } finally {
      setActing(null);
    }
  };

  const handleRevoke = async () => {
    if (!revokeDialogId) return;
    setActing(revokeDialogId);
    try {
      await axios.post(`${API}/requests/${revokeDialogId}/revoke`,
        { reason: revokeReason }, { withCredentials: true });
      toast.success(isAr ? 'تم إلغاء الخطاب' : 'Letter revoked');
      setRevokeDialogId(null);
      setRevokeReason('');
      fetchAll();
      if (activeTab === 'library') fetchLetters();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الإلغاء' : 'Revoke failed'));
    } finally {
      setActing(null);
    }
  };

  const handleDownload = async (req) => {
    try {
      const res = await axios.get(`${API}/requests/${req.id}/attachment`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${req.type_label_en.replace(/ /g, '_')}_${req.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل التنزيل' : 'Download failed'));
    }
  };

  const triggerUpload = (id) => {
    setPendingUpload(id);
    fileInputRef.current?.click();
  };

  const handleFilePicked = async (file) => {
    const id = pendingUpload;
    setPendingUpload(null);
    if (!file || !id) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(isAr ? 'يرجى اختيار ملف PDF' : 'Please select a PDF');
      return;
    }
    // Instead of uploading immediately, stash the picked file + row id
    // and open a dialog letting the admin decide whether the reply PDF
    // should be QR-stamped or delivered as-is. Default: QR enabled.
    setQrDialog({ id, file });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitUpload = async (includeQr) => {
    if (!qrDialog?.id || !qrDialog?.file) return;
    const { id, file } = qrDialog;
    setQrDialog(null);
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/requests/${id}/upload-attachment?include_qr=${includeQr ? 'true' : 'false'}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(isAr
        ? (includeQr ? 'تم رفع الخطاب (مع QR)' : 'تم رفع الخطاب كما هو (بدون QR)')
        : (includeQr ? 'Letter uploaded (with QR)' : 'Letter uploaded as-is (no QR)'));
      fetchAll();
      if (activeTab === 'library') fetchLetters();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الرفع' : 'Upload failed'));
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-2">
          <FileText size={28} weight="duotone" />
          {isAr ? 'طلبات الموظفين' : 'Employee Requests'}
        </h1>

        <Tabs value={activeTab} onValueChange={setActiveTab} dir={direction}>
          <TabsList className="bg-white border border-[#655dc6]/10 w-full sm:w-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="inbox" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0" data-testid="tab-inbox">
              <FileText size={16} weight="duotone" />
              {isITOnly
                ? (isAr ? 'طلبات القسم' : 'Department Requests')
                : (isAr ? 'صندوق الطلبات' : 'Requests Inbox')}
            </TabsTrigger>
            {!isITOnly && (
              <TabsTrigger value="communication" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0" data-testid="tab-communication">
                <ChatCircleDots size={16} weight="duotone" />
                {isAr ? 'تواصل الموظفين' : 'Employee Communication'}
                {communicationItems.filter((r) => r.status === 'pending').length > 0 && (
                  <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5" data-testid="communication-pending-badge">
                    {communicationItems.filter((r) => r.status === 'pending').length}
                  </span>
                )}
              </TabsTrigger>
            )}
            {!isITOnly && (
              <TabsTrigger value="library" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0" data-testid="tab-library">
                <Folders size={16} weight="duotone" />
                {isAr ? 'مكتبة الخطابات' : 'Letters Library'}
                {letters.length > 0 && (
                  <span className="bg-[#655dc6] text-white text-[10px] rounded-full px-1.5 py-0.5">
                    {letters.length}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* INBOX */}
          <TabsContent value="inbox" className="space-y-4 mt-4">

        <Card className="border-[#C4BFF0]" data-testid="filter-card">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 text-[#655dc6]/70 text-sm">
                <Funnel size={16} weight="duotone" />{isAr ? 'الفلاتر:' : 'Filters:'}
              </div>
              <Select value={statusFilter || '_all'} onValueChange={(v) => setStatusFilter(v === '_all' ? '' : v)} dir={direction}>
                <SelectTrigger className="bg-white w-[170px]" data-testid="status-filter">
                  <SelectValue placeholder={isAr ? 'الحالة' : 'Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{isAr ? 'كل الحالات' : 'All statuses'}</SelectItem>
                  <SelectItem value="pending">{isAr ? 'قيد المراجعة' : 'Pending'}</SelectItem>
                  <SelectItem value="approved">{isAr ? 'مقبول' : 'Approved'}</SelectItem>
                  <SelectItem value="rejected">{isAr ? 'مرفوض' : 'Rejected'}</SelectItem>
                  <SelectItem value="cancelled_by_employee">{isAr ? 'ملغى من قبل الموظف' : 'Cancelled by employee'}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter || '_all'} onValueChange={(v) => setTypeFilter(v === '_all' ? '' : v)} dir={direction}>
                <SelectTrigger className="bg-white w-[200px]" data-testid="type-filter">
                  <SelectValue placeholder={isAr ? 'نوع الطلب' : 'Type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{isAr ? 'كل الأنواع' : 'All types'}</SelectItem>
                  {inboxTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{isAr ? t.label_ar : t.label_en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-[#655dc6]/60 ms-auto">
                {isAr ? `${inboxItems.length} طلب` : `${inboxItems.length} requests`}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-[#655dc6] text-base">
              {isAr ? 'القائمة' : 'List'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center"><SpinnerGap size={28} className="animate-spin mx-auto text-[#655dc6]" /></div>
            ) : inboxItems.length === 0 ? (
              <p className="text-center py-12 text-[#655dc6]/60 text-sm">
                {isAr ? 'لا توجد طلبات مطابقة للفلاتر' : 'No requests match the filters'}
              </p>
            ) : (
              <>
              {/* Mobile Card List (<640px) */}
              <div className="sm:hidden space-y-3" data-testid="requests-inbox-mobile-list">
                {inboxItems.map((r) => {
                  const meta = STATUS_META[r.status];
                  const StatusIcon = meta?.Icon || Clock;
                  const dt = fmtKsa(r.submitted_at);
                  return (
                    <div
                      key={r.id}
                      data-testid={`adm-req-card-${r.id}`}
                      className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-bold text-[#262263] dark:text-slate-100">{r.employee_name}</h4>
                          <div className="text-[11px] text-[#655dc6]/70 mt-0.5">{r.department} · #{empNo(r.user_id)}</div>
                          <div className="text-xs text-[#655dc6] mt-1.5 font-medium">
                            {isAr ? r.type_label_ar : r.type_label_en}
                            {r.country && <span className="text-[#655dc6]/60 ms-1">→ {r.country}</span>}
                          </div>
                        </div>
                        <Badge variant="outline" className={`${meta?.cls} gap-1 shrink-0 text-[10px]`}>
                          <StatusIcon size={10} weight="bold" />
                          {isAr ? meta?.ar : meta?.en}
                        </Badge>
                      </div>

                      {(r.purpose || r.admin_reply_text || r.rejection_reason) && (
                        <div className="text-xs text-[#655dc6]/80 mb-3 space-y-1.5">
                          {r.purpose && (
                            <p className="whitespace-pre-wrap break-words leading-relaxed line-clamp-3">{r.purpose}</p>
                          )}
                          {r.admin_reply_text && (
                            <div className="text-[11px] text-emerald-900 bg-emerald-50 px-2 py-1.5 rounded border border-emerald-200 whitespace-pre-wrap break-words leading-relaxed"
                                 data-testid={`adm-reply-mobile-${r.id}`}>
                              <div className="flex items-start justify-between gap-2 mb-0.5">
                                <span className="font-semibold">
                                  {isAr ? 'ردّك: ' : 'Your reply: '}
                                  {r.reply_edited_at && (
                                    <span className="font-normal text-emerald-700/70">
                                      {isAr ? '(تم التعديل)' : '(edited)'}
                                    </span>
                                  )}
                                </span>
                                <button
                                  onClick={() => openEditReply(r)}
                                  className="text-[10px] text-emerald-800 hover:text-emerald-900 hover:underline flex items-center gap-1 shrink-0"
                                  data-testid={`edit-reply-btn-inbox-mobile-${r.id}`}
                                >
                                  <PencilSimple size={10} weight="bold" />
                                  {isAr ? 'تعديل' : 'Edit'}
                                </button>
                              </div>
                              {r.admin_reply_text}
                            </div>
                          )}
                          {r.rejection_reason && (
                            <p className="text-[10px] text-red-700">
                              {isAr ? 'الرفض: ' : 'Reject: '}{r.rejection_reason}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="text-[10px] text-[#655dc6]/60 mb-3">{dt}</div>

                      {(() => {
                        const hasActions =
                          (r.status === 'pending' && !isITOnly) ||
                          r.has_employee_attachment ||
                          (!isITOnly && r.type === 'id_card' && r.status === 'approved' && !r.forwarded_to_it) ||
                          (!isITOnly && r.forwarded_to_it && !r.it_completed_at) ||
                          (isITOnly && r.forwarded_to_it && !r.it_completed_at) ||
                          r.it_completed_at ||
                          r.has_attachment ||
                          (!isITOnly && r.status === 'approved' && !r.has_attachment && r.type !== 'id_card') ||
                          (!isITOnly && r.status === 'approved' && r.has_attachment);
                        if (!hasActions) return null;
                        return (
                      <div className="pt-3 flex flex-wrap items-center gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                        {r.status === 'pending' && !isITOnly && (
                          <>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => {
                                if (r.type === 'other') {
                                  setReplyDialogRow(r);
                                  setReplyText('');
                                } else {
                                  handleApprove(r.id);
                                }
                              }}
                              disabled={acting === r.id}
                              className="min-h-[44px] px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
                              data-testid={`approve-mobile-${r.id}`}
                            >
                              {acting === r.id ? <SpinnerGap size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                              <span className="text-xs">{isAr ? 'موافقة' : 'Approve'}</span>
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => { setRejectDialogId(r.id); setRejectReason(''); }}
                              className="min-h-[44px] px-3 border-red-300 text-red-700 hover:bg-red-50 gap-1"
                              data-testid={`reject-mobile-${r.id}`}
                            >
                              <XCircle size={14} />
                              <span className="text-xs">{isAr ? 'رفض' : 'Reject'}</span>
                            </Button>
                          </>
                        )}
                        {r.has_employee_attachment && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setPhotoDialogUrl(`${process.env.REACT_APP_BACKEND_URL}${r.employee_attachment_url}`)}
                            className="min-h-[44px] px-3 border-purple-300 text-purple-700 hover:bg-purple-50 gap-1"
                            data-testid={`view-photo-mobile-${r.id}`}
                          >
                            <ImageIcon size={14} />
                            <span className="text-xs">{isAr ? 'الصورة' : 'Photo'}</span>
                          </Button>
                        )}
                        {!isITOnly && r.type === 'id_card' && r.status === 'approved' && !r.forwarded_to_it && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleForwardToIT(r.id)}
                            disabled={acting === r.id}
                            className="min-h-[44px] px-3 border-sky-300 text-sky-700 hover:bg-sky-50 gap-1"
                            data-testid={`forward-it-mobile-${r.id}`}
                          >
                            {acting === r.id ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
                            <span className="text-xs">{isAr ? 'تحويل IT' : 'Forward IT'}</span>
                          </Button>
                        )}
                        {!isITOnly && r.forwarded_to_it && !r.it_completed_at && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 border border-sky-200 px-2 py-1 rounded"
                            data-testid={`forwarded-badge-mobile-${r.id}`}
                          >
                            <PaperPlaneTilt size={11} weight="fill" />
                            {isAr ? 'محوّل' : 'Forwarded'}
                          </span>
                        )}
                        {isITOnly && r.forwarded_to_it && !r.it_completed_at && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleITComplete(r.id)}
                            disabled={acting === r.id}
                            className="min-h-[44px] px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
                            data-testid={`it-complete-mobile-${r.id}`}
                          >
                            {acting === r.id ? <SpinnerGap size={14} className="animate-spin" /> : <Package size={14} weight="bold" />}
                            <span className="text-xs">{isAr ? 'تم التسليم' : 'Delivered'}</span>
                          </Button>
                        )}
                        {r.it_completed_at && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded"
                            data-testid={`it-completed-badge-mobile-${r.id}`}
                          >
                            <CheckCircle size={11} weight="fill" />
                            {isAr ? 'مسلَّم' : 'Delivered'}
                          </span>
                        )}
                        {r.has_attachment && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => handleDownload(r)}
                            className="min-h-[44px] px-3 text-[#655dc6] hover:bg-[#E8E6F5] gap-1"
                            data-testid={`download-mobile-${r.id}`}
                          >
                            <Download size={14} />
                            <span className="text-xs">PDF</span>
                          </Button>
                        )}
                        {!isITOnly && r.status === 'approved' && !r.has_attachment && r.type !== 'id_card' && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => triggerUpload(r.id)}
                            disabled={uploadingId === r.id}
                            className="min-h-[44px] px-3 border-purple-300 text-purple-700 hover:bg-purple-50 gap-1"
                            data-testid={`upload-mobile-${r.id}`}
                          >
                            {uploadingId === r.id ? <SpinnerGap size={14} className="animate-spin" /> : <UploadSimple size={14} />}
                            <span className="text-xs">{isAr ? 'رفع PDF' : 'Upload'}</span>
                          </Button>
                        )}
                        {!isITOnly && r.status === 'approved' && r.has_attachment && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => triggerUpload(r.id)}
                            disabled={uploadingId === r.id}
                            className="min-h-[44px] px-3 text-[#655dc6]/60 hover:text-[#655dc6] gap-1"
                            data-testid={`replace-mobile-${r.id}`}
                          >
                            {uploadingId === r.id ? <SpinnerGap size={14} className="animate-spin" /> : <UploadSimple size={14} />}
                            <span className="text-xs">{isAr ? 'استبدال' : 'Replace'}</span>
                          </Button>
                        )}
                      </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table (>=640px) */}
              <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#C4BFF0]/50">
                    <TableHead className="text-start">{isAr ? 'الرقم الوظيفي' : 'Emp #'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'النوع' : 'Type'}</TableHead>
                    <TableHead className="text-start w-[420px]">{isAr ? 'الغرض' : 'Purpose'}</TableHead>
                    <TableHead className="text-center">{isAr ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'التاريخ' : 'Date'}</TableHead>
                    <TableHead className="text-end">{isAr ? 'إجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inboxItems.map((r) => {
                    const meta = STATUS_META[r.status];
                    const StatusIcon = meta?.Icon || Clock;
                    const dt = fmtKsa(r.submitted_at);
                    return (
                      <TableRow key={r.id} className="border-[#C4BFF0]/30" data-testid={`adm-req-row-${r.id}`}>
                        <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(r.user_id)}</TableCell>
                        <TableCell className="font-medium text-[#655dc6]">
                          <p>{r.employee_name}</p>
                          <p className="text-[10px] text-[#655dc6]/60">{r.department}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-[#655dc6]">{isAr ? r.type_label_ar : r.type_label_en}</p>
                          {r.country && <p className="text-[10px] text-[#655dc6]/60">→ {r.country}</p>}
                        </TableCell>
                        <TableCell className="text-xs text-[#655dc6]/80 min-w-[380px] max-w-[520px] whitespace-pre-wrap break-words align-top">
                          <p className="whitespace-pre-wrap break-words leading-relaxed">{r.purpose || '—'}</p>
                          {r.admin_reply_text && (
                            <div className="mt-2 text-[11px] text-emerald-900 bg-emerald-50 px-2 py-1.5 rounded border border-emerald-200 whitespace-pre-wrap break-words leading-relaxed"
                                 data-testid={`adm-reply-${r.id}`}>
                              <div className="flex items-start justify-between gap-2 mb-0.5">
                                <span className="font-semibold">
                                  {isAr ? 'ردّك: ' : 'Your reply: '}
                                  {r.reply_edited_at && (
                                    <span className="font-normal text-emerald-700/70">
                                      {isAr ? '(تم التعديل)' : '(edited)'}
                                    </span>
                                  )}
                                </span>
                                <button
                                  onClick={() => openEditReply(r)}
                                  className="text-[10px] text-emerald-800 hover:text-emerald-900 hover:underline flex items-center gap-1 shrink-0"
                                  data-testid={`edit-reply-btn-inbox-${r.id}`}
                                >
                                  <PencilSimple size={10} weight="bold" />
                                  {isAr ? 'تعديل' : 'Edit'}
                                </button>
                              </div>
                              {r.admin_reply_text}
                            </div>
                          )}
                          {r.rejection_reason && (
                            <p className="text-[10px] text-red-700 mt-1">
                              {isAr ? 'الرفض: ' : 'Reject: '}{r.rejection_reason}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`${meta?.cls} gap-1`}>
                            <StatusIcon size={12} weight="bold" />
                            {isAr ? meta?.ar : meta?.en}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-[#655dc6]/60">{dt}</TableCell>
                        <TableCell className="text-end">
                          <div className="flex justify-end gap-1">
                            {r.status === 'pending' && !isITOnly && (
                              <>
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => {
                                    if (r.type === 'other') {
                                      setReplyDialogRow(r);
                                      setReplyText('');
                                    } else {
                                      handleApprove(r.id);
                                    }
                                  }}
                                  disabled={acting === r.id}
                                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 gap-1"
                                  data-testid={`approve-${r.id}`}
                                >
                                  {acting === r.id ? <SpinnerGap size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                                  {isAr ? 'موافقة' : 'Approve'}
                                </Button>
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => { setRejectDialogId(r.id); setRejectReason(''); }}
                                  className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 gap-1"
                                  data-testid={`reject-${r.id}`}
                                >
                                  <XCircle size={12} />
                                  {isAr ? 'رفض' : 'Reject'}
                                </Button>
                              </>
                            )}
                            {r.has_employee_attachment && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => setPhotoDialogUrl(`${process.env.REACT_APP_BACKEND_URL}${r.employee_attachment_url}`)}
                                className="border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-800 gap-1"
                                data-testid={`view-photo-${r.id}`}
                                title={isAr ? 'عرض الصورة الشخصية المرفقة' : 'View attached photo'}
                              >
                                <ImageIcon size={12} />
                                {isAr ? 'الصورة' : 'Photo'}
                              </Button>
                            )}
                            {/* Admin-only: Forward approved ID-card to IT */}
                            {!isITOnly && r.type === 'id_card' && r.status === 'approved' && !r.forwarded_to_it && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => handleForwardToIT(r.id)}
                                disabled={acting === r.id}
                                className="border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800 gap-1"
                                data-testid={`forward-it-${r.id}`}
                                title={isAr ? 'تحويل إلى مدير تقنية المعلومات' : 'Forward to IT manager'}
                              >
                                {acting === r.id ? <SpinnerGap size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} />}
                                {isAr ? 'تحويل لتقنية المعلومات' : 'Forward to IT'}
                              </Button>
                            )}
                            {/* Admin-only: forwarded badge */}
                            {!isITOnly && r.forwarded_to_it && !r.it_completed_at && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 border border-sky-200 px-2 py-1 rounded"
                                data-testid={`forwarded-badge-${r.id}`}
                              >
                                <PaperPlaneTilt size={11} weight="fill" />
                                {isAr ? 'محوّل لتقنية المعلومات' : 'Forwarded to IT'}
                              </span>
                            )}
                            {/* IT manager: mark as delivered */}
                            {isITOnly && r.forwarded_to_it && !r.it_completed_at && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => handleITComplete(r.id)}
                                disabled={acting === r.id}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 gap-1"
                                data-testid={`it-complete-${r.id}`}
                                title={isAr ? 'إغلاق الطلب بعد التسليم' : 'Mark as delivered'}
                              >
                                {acting === r.id ? <SpinnerGap size={12} className="animate-spin" /> : <Package size={12} weight="bold" />}
                                {isAr ? 'تم التسليم' : 'Delivered'}
                              </Button>
                            )}
                            {/* Completed badge — visible to both admin and IT */}
                            {r.it_completed_at && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded"
                                data-testid={`it-completed-badge-${r.id}`}
                              >
                                <CheckCircle size={11} weight="fill" />
                                {isAr ? `مسلَّم ${(r.it_completed_by_name || '').slice(0, 15)}` : `Delivered${r.it_completed_by_name ? ` · ${r.it_completed_by_name}` : ''}`}
                              </span>
                            )}
                            {r.has_attachment && (
                              <Button size="sm" variant="ghost" onClick={() => handleDownload(r)}
                                      className="text-[#655dc6] hover:text-[#544cb0] hover:bg-[#E8E6F5] gap-1" data-testid={`download-${r.id}`}>
                                <Download size={12} />
                                {isAr ? 'PDF' : 'PDF'}
                              </Button>
                            )}
                            {!isITOnly && r.status === 'approved' && !r.has_attachment && r.type !== 'id_card' && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => triggerUpload(r.id)}
                                disabled={uploadingId === r.id}
                                className="border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-800 gap-1"
                                data-testid={`upload-${r.id}`}
                              >
                                {uploadingId === r.id ? <SpinnerGap size={12} className="animate-spin" /> : <UploadSimple size={12} />}
                                {isAr ? 'رفع PDF' : 'Upload'}
                              </Button>
                            )}
                            {!isITOnly && r.status === 'approved' && r.has_attachment && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => triggerUpload(r.id)}
                                disabled={uploadingId === r.id}
                                className="text-[#655dc6]/60 hover:text-[#655dc6] gap-1"
                                data-testid={`replace-${r.id}`}
                                title={isAr ? 'استبدال الملف' : 'Replace file'}
                              >
                                {uploadingId === r.id ? <SpinnerGap size={12} className="animate-spin" /> : <UploadSimple size={12} />}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* EMPLOYEE COMMUNICATION — free-form Q&A. No approvals, no
              attachments — just text replies from HR. */}
          {!isITOnly && (
            <TabsContent value="communication" className="space-y-4 mt-4">
              <Card className="border-[#C4BFF0]" data-testid="communication-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#655dc6] text-base flex items-center gap-2">
                    <ChatCircleDots size={18} weight="duotone" />
                    {isAr ? 'استفسارات وأسئلة الموظفين' : 'Employee Inquiries & Questions'}
                    <span className="text-xs font-normal text-[#655dc6]/60">
                      · {isAr ? `${communicationItems.length} استفسار` : `${communicationItems.length} inquiries`}
                    </span>
                  </CardTitle>
                  <p className="text-xs text-[#655dc6]/60 leading-relaxed pt-1">
                    {isAr
                      ? 'مساحة تواصل مباشر مع الموظفين للأسئلة العامة. اكتب ردّك النصي وسيصل الموظف مباشرة عبر إشعار.'
                      : 'Direct communication space for general employee questions. Write your text reply and the employee will be notified.'}
                  </p>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="py-8 text-center"><SpinnerGap size={28} className="animate-spin mx-auto text-[#655dc6]" /></div>
                  ) : communicationItems.length === 0 ? (
                    <div className="text-center py-16">
                      <ChatCircleDots size={48} weight="duotone" className="mx-auto text-[#655dc6]/30 mb-2" />
                      <p className="text-sm text-[#655dc6]/60">
                        {isAr ? 'لا توجد استفسارات حتى الآن' : 'No inquiries yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {communicationItems.map((r) => {
                        const meta = STATUS_META[r.status];
                        const StatusIcon = meta?.Icon || Clock;
                        const dt = fmtKsa(r.submitted_at);
                        const answered = r.status === 'approved' || !!r.admin_reply_text;
                        return (
                          <div
                            key={r.id}
                            className="border border-[#C4BFF0]/60 rounded-lg p-4 bg-white space-y-3 hover:shadow-sm transition-shadow"
                            data-testid={`inquiry-${r.id}`}
                          >
                            {/* Header: employee + status + date */}
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold text-[#655dc6] text-sm">
                                  {r.employee_name}
                                  <span className="text-[10px] text-[#655dc6]/60 ms-2 font-normal">
                                    #{empNo(r.user_id)} · {r.department}
                                  </span>
                                </p>
                                <p className="text-[11px] text-[#655dc6]/50">{dt}</p>
                              </div>
                              <Badge variant="outline" className={`${meta?.cls} gap-1 shrink-0`}>
                                <StatusIcon size={12} weight="bold" />
                                {isAr ? meta?.ar : meta?.en}
                              </Badge>
                            </div>

                            {/* Question bubble */}
                            <div className="rounded-md bg-[#F2F0FA] border border-[#C4BFF0]/50 px-3 py-2">
                              <p className="text-[10px] font-semibold text-[#655dc6]/70 mb-1">
                                {isAr ? 'السؤال:' : 'Question:'}
                              </p>
                              <p className="text-sm text-[#2c2668] whitespace-pre-wrap break-words leading-relaxed">
                                {r.purpose || '—'}
                              </p>
                              {/* Inline thumbnail preview of the employee's attached
                                  image (mostly photos for "Other" requests). Click
                                  the thumbnail to open the full-size viewer. */}
                              {r.has_employee_attachment && (
                                <div className="mt-2 pt-2 border-t border-[#C4BFF0]/50">
                                  <p className="text-[10px] font-semibold text-[#655dc6]/70 mb-1.5">
                                    {isAr ? 'صورة مرفقة من الموظف:' : 'Attached photo from the employee:'}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => setPhotoDialogUrl(`${process.env.REACT_APP_BACKEND_URL}${r.employee_attachment_url}`)}
                                    className="group relative inline-block rounded-md overflow-hidden border-2 border-[#C4BFF0] hover:border-[#655dc6] transition-colors focus:outline-none focus:ring-2 focus:ring-[#655dc6]/40"
                                    data-testid={`inquiry-view-employee-attachment-${r.id}`}
                                    title={isAr ? 'اضغط للتكبير' : 'Click to enlarge'}
                                  >
                                    <img
                                      src={`${process.env.REACT_APP_BACKEND_URL}${r.employee_attachment_url}`}
                                      alt={isAr ? 'مرفق الموظف' : 'Employee attachment'}
                                      className="block h-28 w-28 sm:h-32 sm:w-32 object-cover bg-[#F2F0FA]"
                                      loading="lazy"
                                    />
                                    <span className="absolute inset-0 bg-[#655dc6]/0 group-hover:bg-[#655dc6]/25 transition-colors flex items-center justify-center">
                                      <ImageIcon size={22} weight="duotone" className="text-white opacity-0 group-hover:opacity-100 drop-shadow-md transition-opacity" />
                                    </span>
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* HR reply bubble (if any) */}
                            {r.admin_reply_text && (
                              <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <p className="text-[10px] font-semibold text-emerald-800">
                                    {isAr ? 'ردّ شؤون الموظفين:' : 'HR reply:'}
                                    {r.reply_edited_at && (
                                      <span className="ms-2 font-normal text-emerald-700/70">
                                        {isAr ? '(تم التعديل)' : '(edited)'}
                                      </span>
                                    )}
                                  </p>
                                  <button
                                    onClick={() => openEditReply(r)}
                                    className="text-[10px] text-emerald-800 hover:text-emerald-900 hover:underline flex items-center gap-1 shrink-0"
                                    data-testid={`edit-reply-btn-comm-${r.id}`}
                                  >
                                    <PencilSimple size={11} weight="bold" />
                                    {isAr ? 'تعديل الرد' : 'Edit reply'}
                                  </button>
                                </div>
                                <p className="text-sm text-emerald-900 whitespace-pre-wrap break-words leading-relaxed">
                                  {r.admin_reply_text}
                                </p>
                              </div>
                            )}

                            {/* Attachment (PDF) sent as / with the reply.
                                Kept as a distinct chip so the employee-side
                                clearly sees an official document is attached. */}
                            {r.has_attachment && (
                              <div className="rounded-md bg-indigo-50 border border-indigo-200 px-3 py-2 flex items-center justify-between gap-3">
                                <div className="min-w-0 flex items-center gap-2">
                                  <FileText size={18} weight="duotone" className="text-indigo-700 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-semibold text-indigo-800">
                                      {isAr ? 'مرفق الرد (PDF)' : 'Reply attachment (PDF)'}
                                    </p>
                                    <p className="text-[11px] text-indigo-700/80 truncate">
                                      {isAr ? 'ملف مُرفق من قِبَل شؤون الموظفين' : 'File attached by HR'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    size="sm" variant="outline"
                                    onClick={() => handleDownload(r)}
                                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 gap-1"
                                    data-testid={`inquiry-download-${r.id}`}
                                  >
                                    <Download size={12} />
                                    {isAr ? 'تنزيل' : 'Download'}
                                  </Button>
                                  {!isITOnly && (
                                    <Button
                                      size="sm" variant="outline"
                                      onClick={() => { setRevokeDialogId(r.id); setRevokeReason(''); }}
                                      className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 gap-1"
                                      data-testid={`inquiry-revoke-${r.id}`}
                                      title={isAr ? 'إلغاء المرفق' : 'Revoke attachment'}
                                    >
                                      <Prohibit size={12} />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Rejection reason (if closed without reply) */}
                            {r.rejection_reason && (
                              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2">
                                <p className="text-[10px] font-semibold text-red-800 mb-1">
                                  {isAr ? 'سبب الإغلاق:' : 'Closed reason:'}
                                </p>
                                <p className="text-sm text-red-900 whitespace-pre-wrap break-words leading-relaxed">
                                  {r.rejection_reason}
                                </p>
                              </div>
                            )}

                            {/* Actions */}
                            {r.status === 'pending' && (
                              <div className="flex justify-end gap-2 pt-1">
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => { setRejectDialogId(r.id); setRejectReason(''); }}
                                  className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 gap-1"
                                  data-testid={`inquiry-close-${r.id}`}
                                >
                                  <XCircle size={12} />
                                  {isAr ? 'إغلاق بدون رد' : 'Close without reply'}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setReplyDialogRow(r);
                                    setReplyText('');
                                  }}
                                  className="bg-[#655dc6] hover:bg-[#544cb0] gap-1"
                                  data-testid={`inquiry-reply-${r.id}`}
                                >
                                  <PaperPlaneTilt size={12} />
                                  {isAr ? 'رد على السؤال' : 'Reply'}
                                </Button>
                              </div>
                            )}
                            {/* Approved inquiry without an attachment yet — allow HR
                                to upload a supporting PDF as an addendum to the reply. */}
                            {answered && !r.has_attachment && !isITOnly && (
                              <div className="flex justify-end gap-2 pt-1 border-t border-[#C4BFF0]/30">
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => triggerUpload(r.id)}
                                  disabled={uploadingId === r.id}
                                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 gap-1"
                                  data-testid={`inquiry-upload-${r.id}`}
                                >
                                  {uploadingId === r.id
                                    ? <SpinnerGap size={12} className="animate-spin" />
                                    : <UploadSimple size={12} />}
                                  {isAr ? 'إرفاق ملف PDF (اختياري)' : 'Attach PDF (optional)'}
                                </Button>
                              </div>
                            )}
                            {answered && (
                              <p className="text-[10px] text-emerald-700/70 text-end">
                                <CheckCircle size={11} weight="bold" className="inline align-middle -mt-0.5 mx-1" />
                                {isAr ? 'تم الرد على الاستفسار' : 'Inquiry answered'}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* LETTERS LIBRARY */}
          <TabsContent value="library" className="space-y-4 mt-4">
            <Card className="border-[#C4BFF0]" data-testid="library-filter-card">
              <CardContent className="py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1 text-[#655dc6]/70 text-sm">
                    <Funnel size={16} weight="duotone" />
                    {isAr ? 'الفلتر:' : 'Filter:'}
                  </div>
                  <Select value={lettersTypeFilter || '_all'} onValueChange={(v) => setLettersTypeFilter(v === '_all' ? '' : v)} dir={direction}>
                    <SelectTrigger className="bg-white w-[220px]" data-testid="library-type-filter">
                      <SelectValue placeholder={isAr ? 'نوع الخطاب' : 'Letter type'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">{isAr ? 'كل الأنواع' : 'All types'}</SelectItem>
                      {types.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{isAr ? t.label_ar : t.label_en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-[#655dc6]/60 ms-auto">
                    {isAr ? `${letters.length} خطاب` : `${letters.length} letters`}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#C4BFF0]" data-testid="library-card">
              <CardHeader>
                <CardTitle className="text-[#655dc6] text-base flex items-center gap-2">
                  <Folders size={20} weight="duotone" />
                  {isAr ? 'الخطابات الصادرة' : 'Issued Letters'}
                </CardTitle>
                <p className="text-xs text-[#655dc6]/60 mt-1">
                  {isAr
                    ? 'كل الخطابات التي تم رفع نسخها الموقّعة. يمكنك تنزيلها أو استبدالها.'
                    : 'All letters with uploaded signed copies. You can download or replace any of them.'}
                </p>
              </CardHeader>
              <CardContent>
                {lettersLoading ? (
                  <div className="py-8 text-center"><SpinnerGap size={28} className="animate-spin mx-auto text-[#655dc6]" /></div>
                ) : letters.length === 0 ? (
                  <div className="py-12 text-center text-[#655dc6]/60 text-sm">
                    <Folders size={48} weight="duotone" className="mx-auto mb-3 opacity-40" />
                    {isAr ? 'لا توجد خطابات مرفوعة بعد.' : 'No uploaded letters yet.'}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#C4BFF0]/50">
                        <TableHead className="text-start">{isAr ? 'الرقم الوظيفي' : 'Emp #'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'نوع الخطاب' : 'Letter type'}</TableHead>
                        <TableHead className="text-start w-[420px]">{isAr ? 'الغرض' : 'Purpose'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'تاريخ الرفع' : 'Uploaded'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'بواسطة' : 'By'}</TableHead>
                        <TableHead className="text-end">{isAr ? 'إجراءات' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {letters.map((r) => {
                        const dt = fmtKsa(r.reviewed_at || r.submitted_at);
                        const validityLabel = r.is_revoked
                          ? { cls: 'bg-red-50 text-red-700 border-red-200',       txt: isAr ? 'ملغى' : 'Revoked' }
                          : r.is_expired
                            ? { cls: 'bg-amber-50 text-amber-800 border-amber-200', txt: isAr ? 'منتهي' : 'Expired' }
                            : r.expires_at
                              ? { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                                  txt: isAr ? `صالح حتى ${String(r.expires_at).slice(0,10)}` : `Valid to ${String(r.expires_at).slice(0,10)}` }
                              : null;
                        return (
                          <TableRow key={r.id} className="border-[#C4BFF0]/30" data-testid={`lib-row-${r.id}`}>
                            <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(r.user_id)}</TableCell>
                            <TableCell className="font-medium text-[#655dc6]">
                              <p>{r.employee_name}</p>
                              <p className="text-[10px] text-[#655dc6]/60">{r.department}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-[#655dc6]">{isAr ? r.type_label_ar : r.type_label_en}</p>
                              {r.doc_number && (
                                <p className="text-[10px] font-mono text-[#655dc6]/70 mt-0.5"
                                   data-testid={`lib-doc-${r.id}`}>
                                  {r.doc_number}
                                </p>
                              )}
                              {r.country && <p className="text-[10px] text-[#655dc6]/60">→ {r.country}</p>}
                            </TableCell>
                            <TableCell className="text-xs text-[#655dc6]/80 min-w-[380px] max-w-[520px] whitespace-pre-wrap break-words align-top">
                              <p className="whitespace-pre-wrap break-words leading-relaxed">{r.purpose || '—'}</p>
                            </TableCell>
                            <TableCell className="text-xs">
                              <p className="text-[#655dc6]/60">{dt}</p>
                              {validityLabel && (
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded border text-[10px] ${validityLabel.cls}`}>
                                  {validityLabel.txt}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-[#655dc6]/80">{r.reviewed_by_name || '—'}</TableCell>
                            <TableCell className="text-end">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" variant="ghost"
                                        onClick={() => handleDownload(r)}
                                        className="text-[#655dc6] gap-1"
                                        data-testid={`lib-download-${r.id}`}>
                                  <Download size={12} />
                                  {isAr ? 'تنزيل' : 'Download'}
                                </Button>
                                <Button size="sm" variant="ghost"
                                        onClick={() => triggerUpload(r.id)}
                                        disabled={uploadingId === r.id}
                                        className="text-[#655dc6]/60 hover:text-[#655dc6] gap-1"
                                        data-testid={`lib-replace-${r.id}`}
                                        title={isAr ? 'استبدال الملف' : 'Replace file'}>
                                  {uploadingId === r.id ? <SpinnerGap size={12} className="animate-spin" /> : <UploadSimple size={12} />}
                                  {isAr ? 'استبدال' : 'Replace'}
                                </Button>
                                {r.doc_number && !r.is_revoked && (
                                  <Button size="sm" variant="ghost"
                                          onClick={() => { setRevokeDialogId(r.id); setRevokeReason(''); }}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                                          data-testid={`lib-revoke-${r.id}`}
                                          title={isAr ? 'إلغاء الخطاب' : 'Revoke letter'}>
                                    <Prohibit size={12} />
                                    {isAr ? 'إلغاء' : 'Revoke'}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Hidden file input for PDF upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => handleFilePicked(e.target.files?.[0])}
        data-testid="hidden-file-input"
      />

      {/* Employee photo preview dialog (used for ID-card requests) */}
      <Dialog open={!!photoDialogUrl} onOpenChange={(o) => { if (!o) setPhotoDialogUrl(null); }}>
        <DialogContent dir={direction} className="sm:max-w-[560px]" data-testid="photo-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              {isAr ? 'الصورة المرفقة' : 'Attached Photo'}
            </DialogTitle>
            <DialogDescription>
              {isAr ? 'الصورة/الملف الذي أرفقه الموظف مع طلبه.' : 'The image or file the employee attached to their request.'}
            </DialogDescription>
          </DialogHeader>
          {photoDialogUrl && (
            <div className="flex flex-col items-center gap-3">
              <img
                src={photoDialogUrl}
                alt="employee attachment"
                className="max-h-[60vh] w-auto rounded-md border border-[#C4BFF0]"
                data-testid="photo-preview-img"
              />
              <a
                href={photoDialogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#655dc6] underline"
                data-testid="photo-preview-open-new"
              >
                {isAr ? 'فتح في نافذة جديدة' : 'Open in a new tab'}
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve-with-reply dialog (for "other" requests) */}
      <Dialog open={!!replyDialogRow} onOpenChange={(o) => { if (!o) { setReplyDialogRow(null); setReplyText(''); } }}>
        <DialogContent dir={direction} className="sm:max-w-[560px]" data-testid="reply-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">
                {isAr ? 'اعتماد الطلب مع الرد' : 'Approve with reply'}
              </DialogTitle>
              <DialogDescription>
                {isAr
                  ? 'اكتب ردك النصي. سيصل الرد للموظف مباشرة ويظهر ضمن طلباته. يمكنك أيضاً رفع ملف PDF لاحقاً إن لزم.'
                  : "Write your text reply. It will be shown to the employee inside their requests. You can also upload a PDF later if needed."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              {replyDialogRow?.purpose && (
                <div className="p-3 rounded-md bg-[#F5F3FF]/50 border border-[#C4BFF0]/50 text-xs">
                  <div className="text-[#655dc6]/70 mb-1">
                    {isAr ? 'نص الطلب من الموظف:' : "Employee's request:"}
                  </div>
                  <p className="text-[#2D2A5C] whitespace-pre-wrap break-words leading-relaxed">
                    {replyDialogRow.purpose}
                  </p>
                </div>
              )}
              <div>
                <Label className="text-xs text-start block mb-1">
                  {isAr ? 'ردك للموظف *' : 'Your reply *'}
                </Label>
                <Textarea
                  rows={5}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={isAr ? 'مثال: تم قبول طلبك. يُرجى مراجعة قسم الموارد البشرية يوم الأحد لاستلام المستند.' : 'e.g. Your request is accepted. Please visit HR on Sunday to collect the document.'}
                  data-testid="reply-textarea"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setReplyDialogRow(null); setReplyText(''); }} className="border-[#C4BFF0]">
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleApproveWithReply}
                  disabled={acting === replyDialogRow?.id || !replyText.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  data-testid="reply-submit"
                >
                  {acting === replyDialogRow?.id ? <SpinnerGap size={16} className="animate-spin" /> : <CheckCircle size={14} />}
                  {isAr ? 'اعتماد مع الرد' : 'Approve with reply'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit-reply dialog — post-approval edit of admin_reply_text */}
      <Dialog open={!!editReplyRow} onOpenChange={(o) => { if (!o) { setEditReplyRow(null); setEditReplyText(''); } }}>
        <DialogContent dir={direction} className="sm:max-w-[560px]" data-testid="edit-reply-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6] flex items-center gap-2">
                <PencilSimple size={20} weight="duotone" />
                {isAr ? 'تعديل الرد على الطلب' : 'Edit Reply'}
              </DialogTitle>
              <DialogDescription>
                {isAr
                  ? 'يمكنك تعديل ردّك على استفسار الموظف. سيتم إعلامه بأن الرد تم تحديثه.'
                  : "Edit your reply to the employee's request. They will be notified of the update."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              {editReplyRow?.purpose && (
                <div className="rounded-md bg-[#F2F0FA] border border-[#C4BFF0]/50 px-3 py-2 text-xs text-[#655dc6]/80">
                  <p className="font-semibold mb-1 text-[10px] text-[#655dc6]/70">
                    {isAr ? 'استفسار الموظف:' : "Employee's question:"}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{editReplyRow.purpose}</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-start block mb-1">{isAr ? 'الرد الجديد' : 'New reply'}</Label>
                <Textarea
                  rows={5}
                  value={editReplyText}
                  onChange={(e) => setEditReplyText(e.target.value)}
                  placeholder={isAr ? 'اكتب الرد المحدّث…' : 'Type the updated reply…'}
                  data-testid="edit-reply-textarea"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setEditReplyRow(null); setEditReplyText(''); }}
                  className="border-[#C4BFF0]"
                  disabled={editReplySaving}
                  data-testid="edit-reply-cancel"
                >
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleSaveEditedReply}
                  disabled={editReplySaving}
                  className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                  data-testid="edit-reply-save"
                >
                  {editReplySaving ? <SpinnerGap size={16} className="animate-spin" /> : <CheckCircle size={14} />}
                  {isAr ? 'حفظ الرد' : 'Save reply'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialogId} onOpenChange={(o) => { if (!o) setRejectDialogId(null); }}>
        <DialogContent dir={direction} className="sm:max-w-[480px]" data-testid="reject-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">{isAr ? 'رفض الطلب' : 'Reject Request'}</DialogTitle>
              <DialogDescription>
                {isAr ? 'أوضح للموظف سبب الرفض ليتمكن من إعادة التقديم.' : "Explain the reason so the employee can resubmit."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs text-start block mb-1">{isAr ? 'سبب الرفض' : 'Reason'}</Label>
                <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRejectDialogId(null)} className="border-[#C4BFF0]">
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={handleReject} disabled={acting === rejectDialogId}
                        className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="reject-confirm">
                  {acting === rejectDialogId ? <SpinnerGap size={16} className="animate-spin" /> : <XCircle size={14} />}
                  {isAr ? 'تأكيد الرفض' : 'Confirm Reject'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={!!revokeDialogId} onOpenChange={(o) => { if (!o) setRevokeDialogId(null); }}>
        <DialogContent dir={direction} className="sm:max-w-[480px]" data-testid="revoke-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-red-700">
                {isAr ? 'إلغاء الخطاب' : 'Revoke Letter'}
              </DialogTitle>
              <DialogDescription>
                {isAr
                  ? 'سيتم إبطال هذا الخطاب فوراً. أي جهة تمسح رمز QR ستشاهد "خطاب ملغى". لا يمكن التراجع عن هذا الإجراء.'
                  : 'The letter will be marked invalid immediately. Anyone scanning the QR will see "Revoked". This cannot be undone.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs text-start block mb-1">
                  {isAr ? 'سبب الإلغاء (اختياري)' : 'Revoke reason (optional)'}
                </Label>
                <Textarea rows={3} value={revokeReason}
                          onChange={(e) => setRevokeReason(e.target.value)}
                          data-testid="revoke-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRevokeDialogId(null)}
                        className="border-[#C4BFF0]">
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={handleRevoke} disabled={acting === revokeDialogId}
                        className="bg-red-600 hover:bg-red-700 text-white gap-2"
                        data-testid="revoke-confirm">
                  {acting === revokeDialogId ? <SpinnerGap size={16} className="animate-spin" /> : <Prohibit size={14} />}
                  {isAr ? 'تأكيد الإلغاء' : 'Confirm Revoke'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* QR-stamp choice dialog — shown after admin picks a PDF to upload.
          Default action (right-hand primary button) is "stamp with QR"
          to match legacy behaviour; opt-out sends the file untouched. */}
      <Dialog open={!!qrDialog} onOpenChange={(o) => { if (!o) setQrDialog(null); }}>
        <DialogContent dir={direction} className="sm:max-w-[480px]" data-testid="qr-choice-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">
                {isAr ? 'كيف تريد إرسال المستند؟' : 'How should the document be delivered?'}
              </DialogTitle>
              <DialogDescription className="text-start">
                {isAr
                  ? 'يمكن ختم المستند برمز QR للتحقق من صحته، أو إرساله للموظف كما هو مرفوع دون أي تعديل.'
                  : 'You can stamp the document with a verification QR, or send it to the employee exactly as uploaded.'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-2 text-xs text-[#655dc6]/80 bg-[#F2F0FA] border border-[#C4BFF0] rounded p-3">
              <div className="flex items-center gap-2">
                <strong>{isAr ? 'الملف:' : 'File:'}</strong>
                <span className="truncate">{qrDialog?.file?.name}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setQrDialog(null)}
                      className="border-[#C4BFF0]" data-testid="qr-choice-cancel">
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button variant="outline"
                      onClick={() => submitUpload(false)}
                      className="border-[#C4BFF0] text-[#655dc6] hover:bg-[#F2F0FA]"
                      data-testid="qr-choice-no">
                {isAr ? 'إرساله كما هو (بدون QR)' : 'Send as-is (no QR)'}
              </Button>
              <Button onClick={() => submitUpload(true)}
                      className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                      data-testid="qr-choice-yes">
                {isAr ? 'ختم بـ QR ثم إرسال' : 'Stamp with QR & send'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
